import { observer } from "mobx-react";
import type React from "react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import { useGroups } from "hooks/useGroups";

import hogeschooltaalLogo from "assets/images/logo-hogeschooltaal-green.svg";

import { EncircledIcon } from "components/EncircledIcon";
import { Heading2 } from "styles/elements/Headings";
import { ParagraphLarge } from "styles/elements/Texts";
import { CssButton, CssFlex } from "styles/helpers/layout";

import { LoadingSpinner } from "components/LoadingSpinner";

import { GroupEnrollmentOutcome } from "./components/GroupEnrollmentOutcome/GroupEnrollmentOutcome";
import { GroupEnrollmentOutcomeType } from "./components/GroupEnrollmentOutcome/GroupEnrollmentOutcome.types";

import {
  StyledContentContainer,
  StyledGroupName,
  StyledPageContainer,
} from "./JoinGroupPage.styled";

export const JoinGroupPage: React.FC = observer(() => {
  const { t } = useTranslation("groups");
  const { groupId } = useParams<{ groupId: string }>();
  const [activeOutcomeType, setActiveOutcomeType] = useState<GroupEnrollmentOutcomeType | null>(
    null,
  );

  const {
    loading: isLoading,
    groupEnrollmentInfo,
    getGroupEnrollmentInfo,
    joinUserGroup,
  } = useGroups();

  // Fetch information on the group the user wants to join
  useEffect(() => {
    if (groupId) {
      getGroupEnrollmentInfo(+groupId).then((result) => {
        // If action returns no data, it means group fetch failed mostly because it doesn't exist
        if (!result) {
          setActiveOutcomeType(GroupEnrollmentOutcomeType.NotExistingGroup);
        }
      });
    }
  }, [getGroupEnrollmentInfo, groupId]);

  // If we identify user is already part of group, move on to join successful step
  useEffect(() => {
    if (groupEnrollmentInfo?.isAlreadyInGroup) {
      setActiveOutcomeType(GroupEnrollmentOutcomeType.JoinSuccessful);
    }
  }, [groupEnrollmentInfo?.isAlreadyInGroup]);

  // Call endpoint to join group and set right outcome depending on backend response
  const onJoinButtonClick = () => {
    if (groupId) {
      joinUserGroup(+groupId).then((wasSuccessfullyAdded) => {
        setActiveOutcomeType(
          wasSuccessfullyAdded
            ? GroupEnrollmentOutcomeType.JoinSuccessful
            : GroupEnrollmentOutcomeType.NotAllowedToJoin,
        );
      });
    }
  };

  const isGroupJoinable = groupEnrollmentInfo?.id && !groupEnrollmentInfo?.isAlreadyInGroup;

  return (
    <StyledPageContainer>
      <img alt="Hogeschooltaal logo" src={hogeschooltaalLogo} width="200px" />
      {isLoading && <LoadingSpinner />}

      {/* -- Step 1: Show group info with join button -- */}
      {!isLoading && !activeOutcomeType && (
        <StyledContentContainer data-cy="join-group-prompt">
          <Heading2>{t("groups:joinGroup.title")}</Heading2>
          <CssFlex flexDirection="column" alignItems="center" justifyContent="center" gap={1}>
            <EncircledIcon iconName="fas fa-users" variant="secondary" size="large" />
            {groupEnrollmentInfo && (
              <StyledGroupName data-cy="target-group-name">
                {groupEnrollmentInfo.name}
              </StyledGroupName>
            )}
          </CssFlex>
          <ParagraphLarge>{t("groups:joinGroup.description")}</ParagraphLarge>
          <CssButton
            dataCy="join-group-button"
            variant="primary"
            disabled={!isGroupJoinable}
            onClick={onJoinButtonClick}
          >
            {t("groups:joinGroup.buttonLabels.join")}
          </CssButton>
        </StyledContentContainer>
      )}

      {/* -- Step 2: Message indicating join outcome -- */}
      {activeOutcomeType && (
        <GroupEnrollmentOutcome
          groupName={groupEnrollmentInfo?.name}
          outcomeType={activeOutcomeType}
        />
      )}
    </StyledPageContainer>
  );
});
